import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { DragDropContext } from "react-beautiful-dnd";
import ReactTooltip from "react-tooltip";
import { Wrapper, Board } from "./styles/kanban-styles";
import ErrorBoundary from "../shared/error/ErrorBoundary";
import update from "./update";
import updateColumns from "./updateColumns";
import KanbanColumn from "./KanbanColumn";
import ErrorMessage from "./ErrorMessage";
import PositionsFilled from "./PositionsFilled";
import KanbanMobileMenu from "./KanbanMobileMenu";
import KanbanModal from "./KanbanModal";
import KanbanHowTo from "./KanbanHowTo";
import FullScreenLoading from "../shared/loaders/FullScreenLoading";
import SmallUpdating from "../shared/loaders/SmallUpdating";

const emptyCols = [
  {
    columnId: "submitted-candidates",
    title: "Submitted Candidates",
    cardIds: [],
  },
  {
    columnId: "phone-interview",
    title: "Phone interview",
    cardIds: [],
  },
  {
    columnId: "first-interview",
    title: "First face to face interview",
    cardIds: [],
  },
  {
    columnId: "second-interview",
    title: "Second face to face interview",
    cardIds: [],
  },
  {
    columnId: "competencies",
    title: "Competencies",
    cardIds: [],
  },
  {
    columnId: "rejected-candidates",
    title: "Rejected Candidates",
    cardIds: [],
  },
];

const httpEffects = (dispatch, { request, successMsg, errorMsg }) => {
  axios(request)
    .then((response) => dispatch(successMsg(response)))
    .catch((error) => dispatch(errorMsg(error)));
};

class Kanban extends Component {
  static propTypes = {
    isRecruiter: PropTypes.bool,
    cvLimitFull: PropTypes.bool,
    newCandidatesLink: PropTypes.string,
    submittedCandidates: PropTypes.string,
    jobCurrencySymbol: PropTypes.string.isRequired,
    jobId: PropTypes.number,
    engagementId: PropTypes.number,
  };

  static defaultProps = {
    newCandidatesLink: PropTypes.isNull,
    submittedCandidates: PropTypes.isNull,
    isRecruiter: PropTypes.isNull,
    cvLimitFull: PropTypes.isNull,
    engagementId: PropTypes.isNull,
    jobId: PropTypes.isNull,
  };

  constructor(props) {
    super(props);
    const columns = [...emptyCols];

    this.state = {
      columns,
      applications: null,
      cardUpdate: false,
      loading: true,
      updating: false,
      draggedCardData: null,
      draggingActive: false,
      sourceColumnSnapshot: {},
      cardData: {},
      howToModalActive: false,
      positionsFilled: null,
      numberOfPositions: null,
    };
  }

  componentDidMount() {
    document.body.classList.add("candidates-tab");
    this.fetchKanbanData({ loading: true, updating: false });
  }

  componentWillUnmount() {
    document.body.classList.remove("candidates-tab");
  }

  fetchKanbanData = ({ loading, updating }) => {
    this.setState({ loading, updating });

    const { columns } = this.state;
    const { jobId, engagementId, isRecruiter } = this.props;
    const config = { headers: { Accept: "application/json" } };
    const applicationsUrl = isRecruiter
      ? `/recruiter/jobs/${jobId}/engagements/${engagementId}/applications`
      : `/employer/jobs/${jobId}/applications`;

    axios.get(applicationsUrl, config).then(({ data }) => {
      const updatedColumns = updateColumns(columns, data.applications);

      this.setState({
        applications: data.applications,
        columns: updatedColumns,
        loading: false,
        updating: false,
        positionsFilled: data.positions_filled,
        numberOfPositions: data.number_of_positions,
      });
    });
  };

  dispatch = (msg) => {
    this.setState((state) => {
      const { command, ...updatedState } = update(msg, state);
      if (command) {
        httpEffects(this.dispatch, command);
      }
      return updatedState;
    });
  };

  onDragStart = (data) => {
    const { applications } = this.state;
    const { draggableId } = data;
    const cardData = applications.find(
      (application) => application.id === draggableId
    );

    this.setState({ draggingActive: true, cardData });
  };

  onDragEnd = (result) => {
    const { columns } = this.state;
    const { destination, source, draggableId } = result;

    if (destination) {
      if (source.droppableId === destination.droppableId) {
        this.setState({ draggingActive: false });
      }
    } else {
      this.setState({ draggingActive: false });
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const startColumn = columns.filter(
      (el) => el.columnId === source.droppableId
    );
    const finishColumn = columns.filter(
      (el) => el.columnId === destination.droppableId
    );

    // moving in the same column
    if (startColumn === finishColumn) {
      const newCardIds = startColumn[0].cardIds;
      newCardIds.splice(source.index, 1);
      newCardIds.splice(destination.index, 0, draggableId);

      const newColumn = {
        ...startColumn[0],
        cardIds: newCardIds,
      };

      const newState = {
        ...this.state,
        columns: columns.map((oldColumn) => {
          if (oldColumn.columnId === newColumn.columnId) {
            return newColumn;
          }
          return oldColumn;
        }),
      };

      this.setState(newState);
      this.setState({ draggingActive: false });
      return;
    }

    // moving between columns
    const startCardIds = startColumn[0].cardIds;
    startCardIds.splice(source.index, 1);

    const newStartColumn = {
      ...startColumn[0],
      cardIds: startCardIds,
    };

    const finishCardIds = finishColumn[0].cardIds;
    finishCardIds.splice(destination.index, 0, draggableId);

    const newState = {
      ...this.state,
      columns: columns.map((column) => {
        if (column.columnId === newStartColumn.columnId) {
          return newStartColumn;
        }
        return column;
      }),
      draggedCardData: {
        cardUpdateOldColumn: source.droppableId,
        cardUpdateNewColumn: destination.droppableId,
        cardUpdateId: draggableId,
      },
      cardUpdate: source.droppableId !== destination.droppableId && true,
    };

    this.setState(newState);
    this.setState({ draggingActive: false });
  };

  closeActionModalAfterReset = () => {
    // cancel last drag'n'drop - return card back to its drag source column
    const { draggedCardData, columns } = this.state;

    const oldDestinationColumn = columns.map((column) => {
      if (column.columnId === draggedCardData.cardUpdateNewColumn) {
        return {
          cardIds: column.cardIds.filter(
            (cardId) => cardId !== draggedCardData.cardUpdateId
          ),
          columnId: draggedCardData.cardUpdateNewColumn,
          title: draggedCardData.cardUpdateNewColumn.replace("-", " "),
        };
      }
      return null;
    });

    const filteredOldDestinationColumn = oldDestinationColumn.filter(
      (column) => column !== null
    );

    const newState = {
      ...this.state,
      columns: columns.map((column) => {
        // reset drag source column
        if (column.columnId === draggedCardData.cardUpdateOldColumn) {
          column.cardIds.push(draggedCardData.cardUpdateId);
        }
        // reset drop destination column
        if (column.columnId === draggedCardData.cardUpdateNewColumn) {
          return filteredOldDestinationColumn[0];
        }
        return column;
      }),
      cardUpdate: false,
    };
    this.setState(newState);
  };

  // trigger make offer modal on employer side
  handleMakeOffer = (e, cardUpdateId, candidate, updateStatusLink) => {
    e.preventDefault();
    this.setState({
      cardUpdate: true,
      draggedCardData: {
        cardUpdateId,
        cardUpdateNewColumn: "make-offer",
      },
      cardData: { candidate, updateStatusLink },
    });
  };

  // trigger accept/reject job offer modal on recruiter side
  handleJobOffer = (cardUpdateId, candidate, updateStatusLink) => {
    this.setState({
      cardUpdate: true,
      draggedCardData: {
        cardUpdateId,
        cardUpdateNewColumn: "job-offer",
      },
      cardData: { candidate, updateStatusLink },
    });
  };

  // trigger recruiter confirmation modal(s)
  handleRecruiterConfirmation = (
    cardUpdateId,
    candidate,
    updateStatusLink,
    action
  ) => {
    this.setState({
      cardUpdate: true,
      draggedCardData: {
        cardUpdateId,
        cardUpdateNewColumn: action,
      },
      cardData: { candidate, updateStatusLink },
    });
  };

  // close recruiter confirmation modal(s)
  handleCancelRecruiterConfirmation = (e) => {
    e.preventDefault();
    this.setState({
      cardUpdate: false,
      draggedCardData: null,
      cardData: null,
    });
  };

  closeActionModalAfterSubmit = () => this.setState({ cardUpdate: false });

  toggleHowToModal = () => {
    const { howToModalActive } = this.state;

    this.setState(() => ({
      howToModalActive: !howToModalActive,
    }));
  };

  render() {
    const {
      columns,
      applications,
      error,
      cardUpdate,
      draggedCardData,
      cardData,
      loading,
      updating,
      draggingActive,
      howToModalActive,
      positionsFilled,
      numberOfPositions,
    } = this.state;
    const {
      cvLimitFull,
      engagementId,
      isRecruiter,
      jobCurrencySymbol,
      jobId,
      newCandidatesLink,
      submittedCandidates,
    } = this.props;
    const showError = !!error;

    return (
      <ErrorBoundary>
        {updating && <SmallUpdating />}
        {loading ? (
          <FullScreenLoading />
        ) : (
          <DragDropContext
            onDragStart={this.onDragStart}
            onBeforeDragStart={this.onBeforeDragStart}
            onDragEnd={this.onDragEnd}
          >
            <ErrorMessage dispatch={this.dispatch} showError={showError} />
            {cardUpdate && (
              <KanbanModal
                draggedCardData={draggedCardData}
                closeActionModalAfterReset={this.closeActionModalAfterReset}
                closeActionModalAfterSubmit={this.closeActionModalAfterSubmit}
                isRecruiter={isRecruiter}
                cardData={cardData}
                fetchKanbanData={this.fetchKanbanData}
                jobCurrencySymbol={jobCurrencySymbol}
                jobId={jobId}
                engagementId={engagementId}
                handleCancelRecruiterConfirmation={
                  this.handleCancelRecruiterConfirmation
                }
              />
            )}
            <ReactTooltip
              place="left"
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id="positions-filled-tooltip"
            />
            {numberOfPositions > 1 && (
              <PositionsFilled
                positionsFilled={positionsFilled}
                numberOfPositions={numberOfPositions}
              />
            )}
            <KanbanMobileMenu
              toggleHowToModal={this.toggleHowToModal}
              positionsFilled={positionsFilled}
              numberOfPositions={numberOfPositions}
            />
            {!cardUpdate && (
              <KanbanHowTo
                isRecruiter={isRecruiter}
                toggleHowToModal={this.toggleHowToModal}
                howToModalActive={howToModalActive}
              />
            )}
            <Wrapper id="kanban-scroll-area">
              <Board
                draggingActive={draggingActive}
                data-test-id="kanban-board"
              >
                {columns.map(({ columnId, ...column }) => {
                  const cards = column.cardIds
                    .map((cardId) =>
                      applications.find(({ id }) => id === cardId)
                    )
                    .filter((defined) => defined);
                  return (
                    <KanbanColumn
                      draggingActive={draggingActive}
                      key={columnId}
                      id={columnId}
                      column={column}
                      cards={cards}
                      dispatch={this.dispatch}
                      newCandidatesLink={newCandidatesLink}
                      isRecruiter={isRecruiter}
                      cvLimitFull={cvLimitFull}
                      submittedCandidates={submittedCandidates}
                      handleMakeOffer={this.handleMakeOffer}
                      handleJobOffer={this.handleJobOffer}
                      handleRecruiterConfirmation={
                        this.handleRecruiterConfirmation
                      }
                    />
                  );
                })}
              </Board>
            </Wrapper>
          </DragDropContext>
        )}
      </ErrorBoundary>
    );
  }
}

export default Kanban;
